// src/components/AdminUI.jsx
// Shared primitives used across all admin sections.
import React, { useState } from 'react';

// ── Field wrapper ─────────────────────────────────────────────────────────────
export function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 500,
        color: 'var(--sand-muted)',
        fontFamily: "'JetBrains Mono', monospace",
        letterSpacing: '0.5px', marginBottom: 5,
        textTransform: 'uppercase',
      }}>{label}</label>
      {children}
      {hint && (
        <p style={{ fontSize: 11, color: 'var(--sand-hint)', marginTop: 4, fontStyle: 'italic' }}>{hint}</p>
      )}
    </div>
  );
}

// ── Text input ────────────────────────────────────────────────────────────────
export function TextInput({ value, onChange, placeholder, type = 'text', mono = false }) {
  return (
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '8px 12px',
        border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-sm)',
        fontSize: 13,
        fontFamily: mono ? "'JetBrains Mono', monospace" : "'Source Serif 4', serif",
        background: 'var(--sand-bg)', color: 'var(--sand-text)',
        outline: 'none', transition: 'border-color 0.15s',
      }}
      onFocus={e => e.target.style.borderColor = 'var(--sand-amber)'}
      onBlur={e => e.target.style.borderColor = 'var(--sand-border)'}
    />
  );
}

// ── Textarea ──────────────────────────────────────────────────────────────────
export function TextArea({ value, onChange, placeholder, rows = 4, mono = false }) {
  return (
    <textarea
      value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{
        width: '100%', padding: '8px 12px',
        border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-sm)',
        fontSize: 13, lineHeight: 1.6, resize: 'vertical',
        fontFamily: mono ? "'JetBrains Mono', monospace" : "'Source Serif 4', serif",
        background: 'var(--sand-bg)', color: 'var(--sand-text)',
        outline: 'none', transition: 'border-color 0.15s',
      }}
      onFocus={e => e.target.style.borderColor = 'var(--sand-amber)'}
      onBlur={e => e.target.style.borderColor = 'var(--sand-border)'}
    />
  );
}

// ── Primary button ────────────────────────────────────────────────────────────
export function PrimaryBtn({ children, onClick, disabled, danger = false, small = false }) {
  const bg = danger ? '#a32d2d' : 'var(--sand-amber)';
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        padding: small ? '6px 14px' : '9px 20px',
        background: disabled ? 'var(--sand-border)' : bg,
        color: '#fff', border: 'none',
        borderRadius: 'var(--radius-sm)', fontSize: small ? 11 : 13,
        fontFamily: "'Source Serif 4', serif",
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'opacity 0.15s', opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '0.85'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = disabled ? '0.6' : '1'; }}
    >{children}</button>
  );
}

// ── Ghost button ──────────────────────────────────────────────────────────────
export function GhostBtn({ children, onClick, small = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: small ? '5px 12px' : '8px 16px',
        background: 'transparent',
        color: 'var(--sand-muted)',
        border: '0.5px solid var(--sand-border)',
        borderRadius: 'var(--radius-sm)', fontSize: small ? 11 : 13,
        fontFamily: "'Source Serif 4', serif",
        cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--sand-amber)'; e.currentTarget.style.color = 'var(--sand-amber)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--sand-border)'; e.currentTarget.style.color = 'var(--sand-muted)'; }}
    >{children}</button>
  );
}

// ── Toast notification ────────────────────────────────────────────────────────
export function Toast({ message, type = 'success' }) {
  if (!message) return null;
  const colors = {
    success: { bg: '#0a2e2e', color: '#5eead4', border: '#0d4a44' },
    error:   { bg: '#2e0a0a', color: '#fca5a5', border: '#501313' },
    info:    { bg: 'var(--sand-amber-light)', color: 'var(--sand-amber-dark)', border: 'var(--sand-amber-border)' },
  };
  const c = colors[type] || colors.info;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 999,
      padding: '10px 18px', borderRadius: 'var(--radius-md)',
      background: c.bg, color: c.color, border: `0.5px solid ${c.border}`,
      fontSize: 13, fontFamily: "'JetBrains Mono', monospace",
      animation: 'fadeUp 0.25s ease both',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    }}>{message}</div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
export function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,10,20,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)',
        borderRadius: 'var(--radius-lg)', padding: '28px 32px',
        maxWidth: 360, width: '90%',
      }}>
        <p style={{ fontSize: 14, color: 'var(--sand-text)', lineHeight: 1.6, marginBottom: 20 }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <GhostBtn onClick={onCancel}>Cancel</GhostBtn>
          <PrimaryBtn onClick={onConfirm} danger>Delete</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

// ── Tag editor ────────────────────────────────────────────────────────────────
export function TagEditor({ tags, onChange }) {
  const [input, setInput] = useState('');

  function add() {
    const v = input.trim().toLowerCase();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput('');
  }

  function remove(t) {
    onChange(tags.filter(x => x !== t));
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {tags.map(t => (
          <span key={t} style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            padding: '2px 8px', borderRadius: 10,
            background: 'var(--sand-amber-light)', color: 'var(--sand-amber-dark)',
            border: '0.5px solid var(--sand-amber-border)',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            {t}
            <span
              onClick={() => remove(t)}
              style={{ cursor: 'pointer', fontSize: 12, lineHeight: 1 }}
            >×</span>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="Add tag, press Enter"
          style={{
            flex: 1, padding: '6px 10px',
            border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-sm)',
            fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
            background: 'var(--sand-bg)', color: 'var(--sand-text)', outline: 'none',
          }}
        />
        <GhostBtn onClick={add} small>+ Add</GhostBtn>
      </div>
    </div>
  );
}

// ── Image URL list editor ─────────────────────────────────────────────────────
export function ImageListEditor({ images, onChange }) {
  const [input, setInput] = useState('');

  function add() {
    const v = input.trim();
    if (v) onChange([...images, v]);
    setInput('');
  }
  function remove(i) { onChange(images.filter((_, idx) => idx !== i)); }

  return (
    <div>
      {images.map((url, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            color: 'var(--sand-hint)', minWidth: 20,
          }}>{i + 1}</span>
          <span style={{
            flex: 1, fontSize: 11, color: 'var(--sand-muted)',
            fontFamily: "'JetBrains Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{url}</span>
          <GhostBtn onClick={() => remove(i)} small>Remove</GhostBtn>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="Paste image or video URL…"
          style={{
            flex: 1, padding: '6px 10px',
            border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-sm)',
            fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
            background: 'var(--sand-bg)', color: 'var(--sand-text)', outline: 'none',
          }}
        />
        <GhostBtn onClick={add} small>+ Add</GhostBtn>
      </div>
    </div>
  );
}

// ── Section card wrapper ──────────────────────────────────────────────────────
export function AdminCard({ children, style }) {
  return (
    <div style={{
      background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)',
      borderRadius: 'var(--radius-lg)', padding: '22px 26px',
      marginBottom: 20, ...style,
    }}>
      {children}
    </div>
  );
}

// ── Admin section heading ─────────────────────────────────────────────────────
export function AdminHeading({ children }) {
  return (
    <h2 style={{
      fontFamily: "'Playfair Display', serif", fontSize: 18,
      fontWeight: 600, color: 'var(--sand-text)', marginBottom: 18,
    }}>{children}</h2>
  );
}
